import { useMemo } from 'react';
import { useListFloorplansQuery, ListFloorplansQuery } from 'src/graphql/generated/graphql';

/** Raw floorplan entry as returned by the `getFloorplan` metadata query. */
type RawFloorplan = NonNullable<NonNullable<ListFloorplansQuery['getFloorplan']['floorplans']>[number]>;

/**
 * Narrows to the entries the picker can use. Declared at module scope so it is not
 * recreated per render, which would make it a changing `useMemo` dependency below.
 */
const isUsable = (
  fp: RawFloorplan | null | undefined,
): fp is RawFloorplan & { floorplanId: string; goLiveDate: string } =>
  fp?.floorplanId != null && fp?.goLiveDate != null;

/**
 * Lightweight floorplan metadata for the Zone Map floorplan picker.
 *
 * `floorplanId` and `goLiveDate` are non-null here (the picker and its helpers
 * rely on both), even though the underlying `getFloorplan` schema types them as
 * nullable — entries missing either are filtered out below.
 */
export interface FloorplanSummaryItem {
  floorplanId: string;
  name?: string | null;
  externalId?: string | null;
  goLiveDate: string;
  status?: string | null;
  isLive: boolean;
}

interface UseListFloorplansInput {
  storeId: string;
  enabled?: boolean;
}

/**
 * Fetches the floorplan currently in use plus all upcoming floorplans for a
 * store, for the Zone Map floorplan picker.
 *
 * Backed by the consolidated `getFloorplan` query with `includeFuture: true` and
 * no date: the backend anchors on the server's current date, returning the
 * floorplan effective today followed by every future floorplan, sorted by
 * goLiveDate ascending. Passing no date keeps the query key stable. A
 * metadata-only selection set is used, so no fixture documents are fetched.
 */
const useListFloorplans = ({ storeId, enabled = true }: UseListFloorplansInput) => {
  const { data, isSuccess, isLoading } = useListFloorplansQuery(
    {
      input: { storeId, includeFuture: true },
    },
    {
      enabled: enabled && Boolean(storeId),
      refetchOnWindowFocus: false,
    },
  );

  const floorplans: FloorplanSummaryItem[] = useMemo(
    () =>
      (data?.getFloorplan.floorplans ?? []).filter(isUsable).map((fp) => ({
        floorplanId: fp.floorplanId,
        name: fp.name,
        externalId: fp.externalId,
        goLiveDate: fp.goLiveDate,
        status: fp.status,
        isLive: fp.isLive,
      })),
    [data],
  );

  return {
    floorplans,
    isSuccess,
    isLoading,
  };
};

export default useListFloorplans;
